import React, { useEffect } from 'react'
import { FormattedMessage } from 'react-intl'
import { LightAsync as SyntaxHighlighter } from 'react-syntax-highlighter'
import json from 'react-syntax-highlighter/dist/esm/languages/hljs/json'
import { codify } from '../'
import {
    Section, ExternalLink, IconMessage, Heading,
    InlineCode, UnorderedList, BlockCodeWrapper
} from '../s'
import Paragraph from 'components/typography/paragraph'
import { Block as BlockCode } from 'components/code'
import { ListItem } from 'components/list'
import { InfoCircleFilled as Info } from 'components/feather'

const WebhookUserEmailChangeEvent = () => {
    useEffect(() => {
        SyntaxHighlighter.registerLanguage('json', json)
    }, [])

    return (
        <Section>
            <Heading>
                <FormattedMessage
                    id="integration_webhook_heading_profile_email_change_event"
                    defaultMessage="User email address change event" />
            </Heading>
            <Paragraph>
                <FormattedMessage
                    id="integration_webhook_preamble_profile_email_change_event"
                    defaultMessage="These events include a single object <code>user</code>, which identifies the affected user. This object contains:"
                    values={{ code: codify }} />
            </Paragraph>
            <UnorderedList>
                <ListItem>
                    <InlineCode>id</InlineCode>
                    <UnorderedList>
                        <ListItem>
                            <FormattedMessage
                                id="integration_webhook_preamble_how_to_use_3_list_item_1"
                                defaultMessage="Always present" />
                        </ListItem>
                        <ListItem>
                            <span>
                                <FormattedMessage
                                    id="integration_webhook_preamble_how_to_use_3_list_item_3"
                                    defaultMessage="A unique identifier for the user" />
                            </span>
                            <UnorderedList>
                                <ListItem>
                                    <FormattedMessage
                                        id="integration_webhook_preamble_how_to_use_3_list_item_4"
                                        defaultMessage="This value will remain unchanged over the user’s lifetime." />
                                </ListItem>
                            </UnorderedList>
                        </ListItem>
                        <ListItem>
                            <FormattedMessage
                                id="integration_webhook_preamble_how_to_use_3_list_item_5"
                                defaultMessage="Value is a standard 128-bit UUID (see <link>rfc4122</link>)"
                                values={{ link: chunks => (
                                    <ExternalLink href="https://datatracker.ietf.org/doc/html/rfc4122">
                                        {chunks}
                                    </ExternalLink>
                                ) }} />
                        </ListItem>
                    </UnorderedList>
                </ListItem>
                <ListItem>
                    <InlineCode>oldEmail</InlineCode>
                    <UnorderedList>
                        <ListItem>
                            <FormattedMessage
                                id="integration_webhook_preamble_how_to_use_3_list_item_1"
                                defaultMessage="Always present" />
                        </ListItem>
                        <ListItem>
                            <FormattedMessage
                                id="integration_webhook_preamble_old_email"
                                defaultMessage="The email address that previously identified the user" />
                        </ListItem>
                        <ListItem>
                            <FormattedMessage
                                id="integration_webhook_preamble_how_to_use_3_list_item_8"
                                defaultMessage="Value is a string, validated to be an email address" />
                        </ListItem>
                    </UnorderedList>
                </ListItem>
                <ListItem>
                    <InlineCode>newEmail</InlineCode>
                    <UnorderedList>
                        <ListItem>
                            <FormattedMessage
                                id="integration_webhook_preamble_how_to_use_3_list_item_1"
                                defaultMessage="Always present" />
                        </ListItem>
                        <ListItem>
                            <FormattedMessage
                                id="integration_webhook_preamble_new_email"
                                defaultMessage="The email address that identifies the user from now on" />
                        </ListItem>
                        <ListItem>
                            <FormattedMessage
                                id="integration_webhook_preamble_how_to_use_3_list_item_8"
                                defaultMessage="Value is a string, validated to be an email address" />
                        </ListItem>
                    </UnorderedList>
                </ListItem>
                <ListItem>
                    <InlineCode>employmentId</InlineCode>
                    <UnorderedList>
                        <ListItem>
                            <FormattedMessage
                                id="integration_webhook_preamble_how_to_use_3_list_item_9"
                                defaultMessage="Present if set, absent if the user does not have one" />
                        </ListItem>
                        <ListItem>
                            <span>
                                <FormattedMessage
                                    id="integration_webhook_preamble_how_to_use_3_list_item_10"
                                    defaultMessage="A unique identifier at the point where the request is sent: no two users can have the same employment ID" />
                            </span>
                            <UnorderedList>
                                <ListItem>
                                    <FormattedMessage
                                        id="integration_webhook_preamble_how_to_use_3_list_item_11"
                                        defaultMessage="Note that a user’s employment ID can be changed, or removed, by a user with the appropriate permission, so a user is not guaranteed to have the same value in later requests." />
                                </ListItem>
                            </UnorderedList>
                        </ListItem>
                        <ListItem>
                            <FormattedMessage
                                id="integration_webhook_preamble_how_to_use_3_list_item_12"
                                defaultMessage="Value is a string, no restriction other than uniqueness within the organization" />
                        </ListItem>
                    </UnorderedList>
                </ListItem>
            </UnorderedList>
            <IconMessage className="info">
                <Info size={20} />
                <Paragraph className="compact">
                    <FormattedMessage
                        id="integration_webhook_example"
                        defaultMessage="For example" />
                </Paragraph>
            </IconMessage>
            <BlockCodeWrapper>
                <BlockCode
                    language="json"
                    code={codeUserEmailUpdate} />
            </BlockCodeWrapper>
        </Section>
    )
}

const codeUserEmailUpdate = `{
    "topic": "user-email-update",
    "user": {
        "id": "d96434d7-9506-4d5f-a969-7d1eea8bc3d6",
        "oldEmail": "jesper@godmat.no",
        "newEmail": "jesper.blom@godmat.no",
        "employmentId": "1"
    }
}`

export default WebhookUserEmailChangeEvent